import { Component, Input, OnDestroy } from '@angular/core';
import { AiAssistService } from '../ai-assist.service';

interface Lang {
  code: string;
  label: string;
}

/**
 * Reusable flashy AI button + panel. Drop it on any screen:
 *
 *   <app-ai-card
 *     feature="medicine_instructions"
 *     title="Patient Instructions"
 *     buttonLabel="AI Instructions"
 *     icon="✨"
 *     [context]="medicinelist">
 *   </app-ai-card>
 *
 * `context` may be a string, array or object — it is stringified for the AI.
 */
@Component({
  selector: 'app-ai-card',
  templateUrl: './ai-card.component.html',
  styleUrls: ['./ai-card.component.scss'],
})
export class AiCardComponent implements OnDestroy {
  @Input() feature = '';
  @Input() title = 'AI Assistant';
  @Input() buttonLabel = 'Ask AI';
  @Input() icon = '✨';
  @Input() context: any = '';
  /** used when `context` is empty — lets the feature demo without live data */
  @Input() sampleContext: any = '';
  /** show the language toggle (default on) */
  @Input() languages = true;

  usingSample = false;

  open = false;
  loading = false;
  fullText = '';
  shownText = '';
  model = '';
  errored = false;
  lang = 'en';

  readonly langs: Lang[] = [
    { code: 'en', label: 'EN' },
    { code: 'hi', label: 'हिंदी' },
    { code: 'te', label: 'తెలుగు' },
    { code: 'ta', label: 'தமிழ்' },
  ];

  private typer: any = null;

  constructor(private assist: AiAssistService) {}

  ngOnDestroy(): void {
    this.stopTyper();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open && !this.fullText && !this.loading) {
      this.run();
    }
  }

  close(): void {
    this.open = false;
  }

  setLang(code: string): void {
    if (this.lang === code) return;
    this.lang = code;
    this.run();
  }

  run(): void {
    let ctx = this.stringifyContext(this.context);
    this.usingSample = false;
    if (!ctx.trim()) {
      // fall back to a sample so the feature is always demoable
      ctx = this.stringifyContext(this.sampleContext);
      this.usingSample = !!ctx.trim();
    }
    if (!ctx.trim()) {
      this.errored = true;
      this.fullText = this.shownText =
        'Nothing to summarise yet — add details on screen first.';
      return;
    }
    this.loading = true;
    this.errored = false;
    this.fullText = '';
    this.shownText = '';
    this.stopTyper();

    this.assist.generate(this.feature, ctx, this.lang).subscribe({
      next: (res) => {
        this.loading = false;
        this.model = res.model;
        this.errored = !res.ok;
        this.typeOut(res.text || '(no response)');
      },
      error: () => {
        this.loading = false;
        this.errored = true;
        this.typeOut(
          'Could not reach the AI service. Is the AI backend running on :8000?',
        );
      },
    });
  }

  copy(): void {
    if (!this.fullText) return;
    try {
      navigator.clipboard.writeText(this.fullText);
    } catch {
      /* clipboard may be blocked — ignore */
    }
  }

  // ── typewriter ──────────────────────────────────────────────────────────────
  private typeOut(text: string): void {
    this.fullText = text;
    this.shownText = '';
    this.stopTyper();
    let i = 0;
    const step = Math.max(1, Math.round(text.length / 140)); // ~140 frames
    this.typer = setInterval(() => {
      i += step;
      this.shownText = text.slice(0, i);
      if (i >= text.length) {
        this.shownText = text;
        this.stopTyper();
      }
    }, 16);
  }

  private stopTyper(): void {
    if (this.typer) {
      clearInterval(this.typer);
      this.typer = null;
    }
  }

  private stringifyContext(ctx: any): string {
    if (ctx == null) return '';
    if (typeof ctx === 'string') return ctx;
    try {
      return JSON.stringify(ctx, null, 1);
    } catch {
      return String(ctx);
    }
  }
}
